import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import Highlighter from "react-highlight-words";

//UI
import { Table, Empty, message, Row, Col } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  CardElement,
  HomePageTable,
} from "../../../components/Common/UIElements";
import { Input } from "../../../components/Common/Input/index";
import CustomerDataCard from "../../../components/Onboarding/CustomerDataCard/CustomerDataCard";
import LoadingComponent from "../../../components/Common/LoadingComponent";
import { ButtonLink } from "../../../components/Common/Button/index";

//processing
import useQuery from "../../../hooks/url.hooks";
import { tableStringSorter } from "../../../utils/table";
import useTableSearch from "../../../hooks/table-search.hooks";
import {
  RECORD_OPERATIONS,
  ONBOARDING_PROPERTY_STATUS,
} from "../../../utils/onboarding-helper";
import { useApproveSingleProperty } from "../../../hooks/Onboarding/onboarding-properties.hooks";
import { useGetPMS } from "../../../hooks/pms.hooks";

// set table height according to window
let tableHeight = 500;
if (window.innerWidth > 700) {
  tableHeight = window.innerHeight - 375;
}

const PropertyContactDashboard = (props) => {
  const { customer, corporateContact, isLoadingProperties, properties } = props;

  let query = useQuery();
  const msg = query.get("msg");

  useEffect(() => {
    if (msg) message.success(msg);
  }, []);

  const history = useHistory();

  const { data: pmsData } = useGetPMS();

  // Approve Single Property hooks
  const { mutate: approveSingleProperty } = useApproveSingleProperty({
    onSuccess: () => {
      message.success("Property successfully approved");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  // render component for hilightable search text
  const searchHilighter = (dataIndex, customRender) => ({
    render: (text) => {
      return searchedColumns.includes(dataIndex) ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={searchedWords}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : customRender ? (
        customRender(text)
      ) : (
        text
      );
    },
  });

  // table columns
  const columns = [
    {
      title: "Property Name",
      dataIndex: "property_name",
      key: "property_name",
      width: "200px",
      sorter: tableStringSorter("property_name"),
      fixed: "left",
      ...searchHilighter("property_name"),
    },
    {
      title: "Property Code",
      dataIndex: "property_code",
      key: "property_code",
      width: "200px",
      sorter: tableStringSorter("property_code"),
      ...searchHilighter("property_code"),
    },
    {
      title: "Property Brand",
      dataIndex: "brand",
      key: "brand",
      width: "200px",
      sorter: tableStringSorter("brand"),
      ...searchHilighter("brand"),
    },
    {
      title: "PMS",
      dataIndex: "pms_name",
      key: "pms_name",
      width: "200px",
      sorter: tableStringSorter("pms_name"),
      ...searchHilighter("pms_name"),
    },
    {
      title: "POS",
      dataIndex: "pos",
      key: "pos",
      width: "200px",
      sorter: tableStringSorter("pos"),
      ...searchHilighter("pos"),
    },
    {
      title: "Time Zone",
      dataIndex: "property_time_zone",
      key: "property_time_zone",
      width: "200px",
      sorter: tableStringSorter("property_time_zone"),
      ...searchHilighter("property_time_zone"),
    },
    {
      title: "Property Contact First Name",
      dataIndex: "property_contact_first_name",
      key: "property_contact_first_name",
      width: "200px",
      sorter: tableStringSorter("property_contact_first_name"),
      ...searchHilighter("property_contact_first_name"),
    },
    {
      title: "Property Contact Last Name",
      dataIndex: "property_contact_last_name",
      key: "property_contact_last_name",
      width: "200px",
      sorter: tableStringSorter("property_contact_last_name"),
      ...searchHilighter("property_contact_last_name"),
    },
    {
      title: "Property Contact Email",
      dataIndex: "property_contact_email",
      key: "property_contact_email",
      width: "200px",
      sorter: tableStringSorter("property_contact_email"),
      ...searchHilighter("property_contact_email"),
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "_id",
      width: "200px",
      render: (_id, record) => {
        const status = record.status;
        return (
          <Row>
            {status === ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL && (
              <Col>
                <ButtonLink
                  onClick={() => {
                    approveSingleProperty(_id);
                  }}
                >
                  <span style={{ textDecoration: "underline" }}>Approve</span>
                </ButtonLink>
              </Col>
            )}
            <Col>
              <ButtonLink
                disabled
                onClick={() => {
                  history.push(
                    `/onboarding/prop-contact/property/${RECORD_OPERATIONS.EDIT}?property=${record._id}`,
                  );
                }}
              >
                <span style={{ textDecoration: "underline" }}>Edit</span>
              </ButtonLink>
            </Col>
          </Row>
        );
      },
      fixed: "right",
    },
  ];

  // columns that could be searched
  const searchableColumns = columns.map((col) => col.dataIndex);

  let propertiesData = useMemo(() => {
    return properties?.map((property) => {
      const baseProperty = { ...property };
      const propContact = baseProperty.property_contact_id;
      const pms = pmsData?.filter((pms) => pms?._id === property?.pms)[0];

      return {
        _id: property._id,
        property_name: property.property_name,
        property_code: property.property_code,
        brand: property.brand,
        pms: property.pms,
        pms_name: pms?.pms_name,
        pos: property.pos,
        property_time_zone: property.property_time_zone,
        property_contact_first_name: propContact?.first_name,
        property_contact_last_name: propContact?.last_name,
        property_contact_email: propContact?.email,
        customer_id: property.customer_id,
        status: property.status,
      };
    });
  }, [properties, pmsData]);

  // custom hook for external multi column table data searching
  const {
    filteredTableData,
    searchedColumns,
    searchedWords,
    updateSearchMeta,
    searchMeta,
  } = useTableSearch({
    searchableColumns,
    allData: propertiesData,
  });

  if (Object.keys(searchMeta).length > 0 && filteredTableData) {
    propertiesData = [...filteredTableData];
  }

  return (
    <CardElement title="System Discovery">
      <Row>
        <Col span={24}>
          <h2>Hotel Management Group</h2>
        </Col>
        <hr />
      </Row>
      <CustomerDataCard
        customer={customer}
        corporateContact={corporateContact}
      />
      <Row>
        <Col span={24}>
          <h2>Property List</h2>
        </Col>
        <hr />
      </Row>
      <Row style={{ marginBottom: "20px" }}>
        <Col
          xxl={{ span: 4, offset: 20 }}
          xl={{ span: 4, offset: 20 }}
          lg={{ span: 4, offset: 20 }}
          md={{ span: 4, offset: 20 }}
          sm={24}
          xs={24}
        >
          <Input
            name="searchAll"
            placeholder="Search"
            className="onboarding-toolbar-input"
            prefix={<SearchOutlined />}
            onChange={(e) => {
              updateSearchMeta(e.target.value, "searchAll");
            }}
            allowClear
          />
        </Col>
      </Row>
      <Row>
        <div style={{ width: "100%" }}>
          {!Array.isArray(propertiesData) ? (
            <Empty description="No such property found" />
          ) : (
            <div>
              <HomePageTable>
                <Table
                  className="onboarding-properties-table"
                  columns={columns}
                  dataSource={propertiesData}
                  rowKey="_id"
                  scroll={{ x: 1100, y: tableHeight }}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "25", "50", "100"],
                  }}
                  locale={{
                    emptyText: isLoadingProperties ? (
                      <div className="spinner-table">
                        <LoadingComponent />
                      </div>
                    ) : null,
                  }}
                />
              </HomePageTable>
            </div>
          )}
        </div>
      </Row>
    </CardElement>
  );
};

export default PropertyContactDashboard;
